import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import API_BASE_URL from "./config/api";
import "./MyTrips.css";

function MyTrips() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Auto-load if email is saved from a previous booking
  useEffect(() => {
    const savedEmail = localStorage.getItem("trippro_userEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      fetchProfile(savedEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = useCallback(async (lookupEmail) => {
    if (!lookupEmail || !lookupEmail.trim()) return;

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/customers/lookup?email=${encodeURIComponent(lookupEmail.trim())}`
      );

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        // Remember for future visits
        localStorage.setItem("trippro_userEmail", lookupEmail.trim().toLowerCase());
        if (data.userId) {
          localStorage.setItem("trippro_userId", data.userId);
        }
      } else {
        const err = await response.json();
        setProfile(null);
        setError(err.message || "No trips found for this email.");
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
      setProfile(null);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }
    fetchProfile(email);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatPrice = (price) => {
    if (!price) return "N/A";
    return `₹${Math.round(price).toLocaleString("en-IN")}`;
  };

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <div className="mytrips-page">
      <Navbar />

      {/* Hero */}
      {profile ? (
        <div className="mytrips-hero">
          <h1>Welcome back, {profile.name?.split(" ")[0]}!</h1>
          <p>Here's a summary of your trips and travel preferences.</p>
          <div className="user-badge">
            🆔 {profile.userId} &nbsp;•&nbsp; 📧 {profile.email}
          </div>
        </div>
      ) : (
        <div className="mytrips-hero">
          <h1>My Trips</h1>
          <p>View all your bookings and travel preferences in one place.</p>
        </div>
      )}

      {/* Main Content */}
      {!profile && !loading && (
        <div className="email-lookup-section">
          <div className="email-lookup-card">
            <h2>Find Your Trips</h2>
            <p>
              Enter the email address you used while booking to view all your
              trips and travel preferences.
            </p>
            <form onSubmit={handleSubmit}>
              <div className="lookup-input-group">
                <input
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(""); }}
                  required
                />
                <button type="submit" className="btn-lookup" disabled={loading}>
                  {loading ? "Searching..." : "Find Trips"}
                </button>
              </div>
            </form>
            {error && <p className="lookup-error">{error}</p>}
          </div>
        </div>
      )}

      {loading && (
        <div className="mytrips-loading">
          <div className="mytrips-spinner"></div>
          <p style={{ color: "#666" }}>Looking up your trips...</p>
        </div>
      )}

      {profile && (
        <div className="mytrips-container">

          {/* Bookings Section */}
          <div className="section-title">
            🎫 Your Bookings
            <span className="count-badge">{profile.bookings?.length || 0}</span>
          </div>

          {profile.bookings && profile.bookings.length > 0 ? (
            [...profile.bookings]
              .sort((a, b) => new Date(b.bookedAt) - new Date(a.bookedAt))
              .map((booking, i) => {
                const pkg = booking.packageId;
                return (
                  <div
                    key={booking.bookingId || i}
                    className="booking-card"
                    onClick={() => {
                      if (pkg?._id) {
                        navigate("/tripdetails", {
                          state: { selectedPackage: pkg._id },
                        });
                      }
                    }}
                  >
                    <img
                      src={pkg?.coverImage || "https://images.unsplash.com/photo-1628155930542-3c7a64e2c833?w=300&auto=format&fit=crop&q=60"}
                      alt={pkg?.name || "Trip"}
                      className="booking-cover"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1628155930542-3c7a64e2c833?w=300&auto=format&fit=crop&q=60";
                      }}
                    />
                    <div className="booking-info">
                      <h3>{pkg?.name || `Trip Booking`}</h3>
                      <div className="booking-meta">
                        <span>📅 {formatDate(booking.bookedAt)}</span>
                        <span className="booking-id-badge">ID: {booking.bookingId}</span>
                        {booking.occasion && <span>🎯 {booking.occasion}</span>}
                        {booking.duration > 0 && <span>⏱ {booking.duration} Days</span>}
                      </div>
                      <div className="booking-destinations">
                        {(booking.destinations || []).map((dest, j) => (
                          <span key={j} className="dest-chip">{dest}</span>
                        ))}
                      </div>
                    </div>
                    <div className="booking-price">
                      {formatPrice(booking.totalPrice)}
                    </div>
                  </div>
                );
              })
          ) : (
            <div className="empty-state">
              <div className="empty-icon">🗺️</div>
              <p>No bookings yet. Start planning your first trip!</p>
            </div>
          )}

          {/* Travel Preferences */}
          {profile.preferences && profile.preferences.totalTrips > 0 && (
            <>
              <div className="section-title">📊 Your Travel Profile</div>
              <div className="preferences-card">

                <div className="pref-grid">
                  <div className="pref-item">
                    <div className="pref-label">Total Trips</div>
                    <div className="pref-value">{profile.preferences.totalTrips}</div>
                  </div>
                  <div className="pref-item">
                    <div className="pref-label">Total Spent</div>
                    <div className="pref-value">{formatPrice(profile.preferences.totalSpent)}</div>
                  </div>
                  <div className="pref-item">
                    <div className="pref-label">Avg. per Trip</div>
                    <div className="pref-value">{formatPrice(profile.preferences.averageSpendPerTrip)}</div>
                  </div>
                  <div className="pref-item">
                    <div className="pref-label">Preferred Duration</div>
                    <div className="pref-value">
                      {profile.preferences.preferredDuration
                        ? `${profile.preferences.preferredDuration} Days`
                        : "—"}
                    </div>
                  </div>
                  <div className="pref-item">
                    <div className="pref-label">Budget Tier</div>
                    <div className="pref-value small">{profile.preferences.preferredBudgetTier || "—"}</div>
                  </div>
                  <div className="pref-item">
                    <div className="pref-label">Preferred Pace</div>
                    <div className="pref-value small">{profile.preferences.preferredPace || "—"}</div>
                  </div>
                </div>

                {/* Favorite Destinations */}
                {profile.preferences.favoriteDestinations?.length > 0 && (
                  <div style={{ marginTop: "20px" }}>
                    <div className="pref-label" style={{ fontSize: "12px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600, marginBottom: "8px" }}>
                      Favorite Destinations
                    </div>
                    <div className="fav-dest-list">
                      {profile.preferences.favoriteDestinations.map((d, i) => (
                        <span key={i} className="fav-dest-chip">
                          {d.name} ({d.count}×)
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Travel Months */}
                {profile.preferences.preferredTravelMonths?.length > 0 && (
                  <div style={{ marginTop: "16px" }}>
                    <div className="pref-label" style={{ fontSize: "12px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600, marginBottom: "8px" }}>
                      Usually Travels In
                    </div>
                    <div className="fav-dest-list">
                      {profile.preferences.preferredTravelMonths.map((m, i) => (
                        <span key={i} className="fav-dest-chip">
                          {monthNames[m - 1] || m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Occasions */}
                {profile.preferences.occasions?.length > 0 && (
                  <div style={{ marginTop: "16px" }}>
                    <div className="pref-label" style={{ fontSize: "12px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600, marginBottom: "8px" }}>
                      Trip Occasions
                    </div>
                    <div className="fav-dest-list">
                      {profile.preferences.occasions.map((o, i) => (
                        <span key={i} className="fav-dest-chip">
                          {o.type} ({o.count}×)
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <Footer />
    </div>
  );
}

export default MyTrips;
